import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  Paper,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import Fireworks from './Fireworks';

interface ContactProps {
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
}

const Contact: React.FC<ContactProps> = ({ maxWidth = 'lg' }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    companySize: '',
    industry: '',
    services: [] as string[],
    message: '',
    contactMethod: 'email',
    bestTime: '',
    jobTitle: '',
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [submitMessage, setSubmitMessage] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const countryCodes = [
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+1', country: 'USA', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+55', country: 'Brazil', flag: '🇧🇷' },
    { code: '+7', country: 'Russia', flag: '🇷🇺' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { code: '+66', country: 'Thailand', flag: '🇹🇭' },
    { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
    { code: '+63', country: 'Philippines', flag: '🇵🇭' },
    { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
  ];

  const sanitizeInput = (input: string): string => {
    return input
      .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remove script tags
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .trim();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    
    // Sanitize input
    const sanitizedValue = sanitizeInput(value);
    
    // Format phone number - only allow digits
    if (name === 'phone') {
      const digitsOnly = sanitizedValue.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Name validation
    if (!formData.fullName || formData.fullName.length < 2) {
      newErrors.fullName = 'Full name is required (minimum 2 characters)';
    }
    
    // Company validation
    if (!formData.companyName || formData.companyName.length < 2) {
      newErrors.companyName = 'Company name is required';
    }
    
    // Phone validation
    if (!formData.phone) {
      newErrors.phone = 'Mobile number is required';
    } else if (formData.phone.length < 7 || formData.phone.length > 15) {
      newErrors.phone = 'Please enter a valid mobile number';
    }
    
    // Best time validation
    if (!formData.bestTime) {
      newErrors.bestTime = 'Please select your preferred contact time';
    }
    
    // Services validation
    if (formData.services.length === 0) {
      newErrors.services = 'Please select at least one service of interest';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      setSubmitMessage('Please fix the errors above before submitting.');
      setShowError(true);
      
      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://formspree.io/f/xwpqowne', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.fullName,
          company: formData.companyName,
          email: formData.email,
          phone: countryCode + formData.phone,
          companySize: formData.companySize,
          industry: formData.industry,
          services: formData.services.join(', '),
          contactMethod: formData.contactMethod,
          bestTime: formData.bestTime,
          message: formData.message,
          jobTitle: formData.jobTitle,
          _subject: `New Contact Form Submission from ${formData.companyName}`,
        }),
      });
      
      if (response.ok) {
        setSubmitMessage(`🎉 Thank you, ${formData.fullName}! Your quote request has been submitted successfully.`);
        setShowFireworks(true);
        setShowSuccess(true);
        setIsSubmitted(true);
        
        // Reset form after delay
        setTimeout(() => {
          setFormData({
            fullName: '',
            companyName: '',
            email: '',
            phone: '',
            companySize: '',
            industry: '',
            services: [],
            message: '',
            contactMethod: 'email',
            bestTime: '',
            jobTitle: '',
          });
          setIsSubmitted(false);
        }, 5000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage('There was an issue submitting your form. Opening your email client as backup...');
      setShowError(true);
      
      // Fallback to mailto after short delay
      setTimeout(() => {
        const emailSubject = `New Contact Form Submission from ${formData.companyName}`;
        const emailBody = `
New contact form submission:

Name: ${formData.fullName}
Company: ${formData.companyName}
Email: ${formData.email}
Phone: ${countryCode}${formData.phone}
Company Size: ${formData.companySize}
Industry: ${formData.industry}
Services Interested: ${formData.services.join(', ')}
Preferred Contact: ${formData.contactMethod}
Best Time: ${formData.bestTime}
Job Title/Role: ${formData.jobTitle}

Message:
${formData.message}
        `;
        const mailtoLink = `mailto:njinabad@tncosoft.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
        window.location.href = mailtoLink;
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <PhoneIcon />,
      title: 'Phone',
      details: ['+91-8999096807']
    },
    {
      icon: <EmailIcon />,
      title: 'Email',
      details: ['njinabad@tncosoft.com']
    },
    {
      icon: <ScheduleIcon />,
      title: 'Business Hours',
      details: ['Mon-Sat: 9:00 AM - 7:00 PM', 'Sun: Closed']
    },
  ];

  const serviceOptions = [
    'Virtualization Solutions',
    'Private Cloud Infrastructure',
    'Disaster Recovery Planning',
    'Enterprise Data Backup',
    'Consumption-based IT (as-a-Service)',
    'Infrastructure Assessment'
  ];

  return (
    <Box id="contact" sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.default' }}>
      <Container maxWidth={maxWidth}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" component="h2" gutterBottom>
            Get Quote Now!
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Contact our infrastructure experts for a personalized quote and consultation.
          </Typography>
        </Box>

        <Grid container spacing={6}>
          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 4 }}>
              {contactInfo.map((info, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'flex-start', p: 2 }}>
                    <Box sx={{ color: 'primary.main', mr: 2, mt: 0.5 }}>
                      {info.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {info.title}
                      </Typography>
                      {info.details.map((detail, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          {detail}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Paper sx={{ p: 3, backgroundColor: 'primary.main', color: 'white' }}>
              <Typography variant="h6" gutterBottom>
                Talk to an Expert
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Nohid
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                📧 njinabad@tncosoft.com
              </Typography>
              <Typography variant="body2">
                📞 +91-8999096807
              </Typography>
            </Paper>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom>
                  Request Your Quote
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Full Name"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        error={!!errors.fullName}
                        helperText={errors.fullName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Company Name"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        error={!!errors.companyName}
                        helperText={errors.companyName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        error={!!errors.email}
                        helperText={errors.email}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <FormControl sx={{ minWidth: 120 }}>
                          <InputLabel>Country</InputLabel>
                          <Select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            label="Country"
                            size="medium"
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 300,
                                  overflow: 'auto',
                                },
                              },
                            }}
                          >
                            {countryCodes.map((country) => (
                              <MenuItem key={country.code} value={country.code}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span>{country.flag}</span>
                                  <span>{country.code}</span>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          required
                          fullWidth
                          label="Mobile Number"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="Enter mobile number"
                          error={!!errors.phone}
                          helperText={errors.phone}
                          inputProps={{
                            maxLength: 15
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required error={!!errors.companySize}>
                        <InputLabel>Company Size</InputLabel>
                        <Select
                          name="companySize"
                          value={formData.companySize}
                          onChange={handleSelectChange}
                          label="Company Size"
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                overflow: 'auto',
                              },
                            },
                          }}
                        >
                          <MenuItem value="1-50">1-50 employees</MenuItem>
                          <MenuItem value="51-200">51-200 employees</MenuItem>
                          <MenuItem value="201-1000">201-1000 employees</MenuItem>
                          <MenuItem value="1000+">1000+ employees</MenuItem>
                        </Select>
                        {errors.companySize && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            {errors.companySize}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required error={!!errors.industry}>
                        <InputLabel>Industry</InputLabel>
                        <Select
                          name="industry"
                          value={formData.industry}
                          onChange={handleSelectChange}
                          label="Industry"
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                overflow: 'auto',
                              },
                            },
                          }}
                        >
                          <MenuItem value="financial">Financial Services</MenuItem>
                          <MenuItem value="healthcare">Healthcare</MenuItem>
                          <MenuItem value="manufacturing">Manufacturing</MenuItem>
                          <MenuItem value="technology">Technology</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                        {errors.industry && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            {errors.industry}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Services of Interest *
                      </Typography>
                      {errors.services && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                          {errors.services}
                        </Typography>
                      )}
                      <FormGroup row>
                        {serviceOptions.map((service) => (
                          <FormControlLabel
                            key={service}
                            control={
                              <Checkbox
                                checked={formData.services.includes(service)}
                                onChange={() => handleServiceChange(service)}
                              />
                            }
                            label={service}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required error={!!errors.bestTime}>
                        <InputLabel>Best Time to Contact</InputLabel>
                        <Select
                          name="bestTime"
                          value={formData.bestTime}
                          onChange={handleSelectChange}
                          label="Best Time to Contact"
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                overflow: 'auto',
                              },
                            },
                          }}
                        >
                          <MenuItem value="morning">Morning (9 AM - 12 PM)</MenuItem>
                          <MenuItem value="afternoon">Afternoon (12 PM - 5 PM)</MenuItem>
                          <MenuItem value="evening">Evening (5 PM - 8 PM)</MenuItem>
                          <MenuItem value="anytime">Anytime</MenuItem>
                        </Select>
                        {errors.bestTime && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            {errors.bestTime}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required error={!!errors.jobTitle}>
                        <InputLabel>Your Role/Position</InputLabel>
                        <Select
                          name="jobTitle"
                          value={formData.jobTitle}
                          onChange={handleSelectChange}
                          label="Your Role/Position"
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                overflow: 'auto',
                              },
                            },
                          }}
                        >
                          <MenuItem value="ceo">CEO/Founder</MenuItem>
                          <MenuItem value="cto">CTO/Chief Technology Officer</MenuItem>
                          <MenuItem value="cio">CIO/Chief Information Officer</MenuItem>
                          <MenuItem value="it-director">IT Director</MenuItem>
                          <MenuItem value="it-manager">IT Manager</MenuItem>
                          <MenuItem value="infrastructure-manager">Infrastructure Manager</MenuItem>
                          <MenuItem value="operations-director">Operations Director</MenuItem>
                          <MenuItem value="system-admin">System Administrator</MenuItem>
                          <MenuItem value="admin">Administrator</MenuItem>
                          <MenuItem value="consultant">Consultant</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                        {errors.jobTitle && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            {errors.jobTitle}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Current Infrastructure Challenges"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your current infrastructure needs or challenges..."
                      />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isSubmitting || isSubmitted}
                    sx={{ 
                      mt: 4, 
                      py: 1.5,
                      bgcolor: isSubmitted ? 'success.main' : 'primary.main',
                      transition: 'all 0.3s ease',
                      transform: isSubmitted ? 'scale(1.02)' : 'scale(1)',
                      '&:hover': {
                        bgcolor: isSubmitted ? 'success.dark' : 'primary.dark',
                        transform: isSubmitted ? 'scale(1.02)' : 'scale(1.05)'
                      },
                      '&:disabled': {
                        bgcolor: isSubmitted ? 'success.main' : 'grey.400',
                        color: 'white'
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                        Submitting Request...
                      </>
                    ) : isSubmitted ? (
                      '✅ Request Submitted Successfully!'
                    ) : (
                      'Request Quote'
                    )}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      
      {/* Fireworks Animation */}
      <Fireworks 
        show={showFireworks} 
        onComplete={() => setShowFireworks(false)} 
      />
      
      {/* Success Toast */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10001,
        }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          sx={{ 
            width: '100%',
            fontSize: { xs: '0.9rem', md: '1.1rem' },
            maxWidth: { xs: '90vw', md: '600px' },
            boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
            border: '2px solid #4caf50',
            animation: 'slideInDown 0.5s ease-out',
            '@keyframes slideInDown': {
              '0%': {
                transform: 'translateY(-100px)',
                opacity: 0
              },
              '100%': {
                transform: 'translateY(0)',
                opacity: 1
              }
            },
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              fontWeight: 600
            }
          }}
        >
          {submitMessage}
        </Alert>
      </Snackbar>
      
      {/* Error Toast */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10001,
        }}
      >
        <Alert 
          onClose={() => setShowError(false)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {submitMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;